'use client'

import { useTranslation } from 'react-i18next'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ProcessorDetails } from '@/lib/api/processors'

interface SandboxConfigurationCardProps {
    processor: ProcessorDetails
}

const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    })
}

export const SandboxConfigurationCard = ({ processor }: SandboxConfigurationCardProps) => {
    const { t } = useTranslation('processors')
    const options = processor.options as Record<string, unknown>
    const accountId = options?.accountId as string | undefined

    return (
        <Card className="mb-6">
            <CardHeader>
                <CardTitle>{t('sandboxConfiguration')}</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-6">
                    <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
                        <p className="text-sm text-blue-700">
                            🧪 {t('sandboxEnvironmentNote')}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <p className="text-sm text-muted-foreground">{t('environment')}</p>
                            <Badge variant="secondary">{t('testEnvironment')}</Badge>
                        </div>

                        <div>
                            <p className="text-sm text-muted-foreground">{t('accountId')}</p>
                            <p className="font-mono text-sm">
                                {accountId || t('notConfigured')}
                            </p>
                        </div>

                        <div>
                            <p className="text-sm text-muted-foreground">{t('apiVersion')}</p>
                            <p className="font-semibold">v1.0</p>
                        </div>

                        <div>
                            <p className="text-sm text-muted-foreground">{t('baseCurrency')}</p>
                            <p className="font-semibold">{processor.baseCurrency || 'USD'}</p>
                        </div>

                        <div>
                            <p className="text-sm text-muted-foreground">{t('createdAt')}</p>
                            <p className="font-semibold">{formatDate(processor.createdAt)}</p>
                        </div>

                        {processor.updatedAt && (
                            <div>
                                <p className="text-sm text-muted-foreground">{t('updatedAt')}</p>
                                <p className="font-semibold">{formatDate(processor.updatedAt)}</p>
                            </div>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}

